import { useCallback, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Box } from "@mui/material";
import { changeLanguage } from "i18next";
import { useTranslation } from "react-i18next";

import { CustomTooltip } from "components";
import { updateLanguage, createItemRecord, formatMessageEdit } from "utils";
import {
  getSpanishLanguage,
  getHeaderBgColor,
  selectPropertyId,
  selectAPIKey,
  selectIsDisabled,
  setIsSpanishLanguage,
  getAppSSID,
  addNewMessage,
} from "slices";

export const LanguageSwitcher = () => {
  const dispatch = useDispatch();
  const { i18n } = useTranslation();

  const isSpanishCurrentLanguage = useSelector(getSpanishLanguage);
  const backgroundColor = useSelector(getHeaderBgColor);
  const propertyId = useSelector(selectPropertyId);
  const apiKey = useSelector(selectAPIKey);
  const SSID = useSelector(getAppSSID);
  const isDisabledOptions = useSelector(selectIsDisabled);

  const isEnglishCurrentLanguage = !isSpanishCurrentLanguage;

  const handleLanguageChange = useCallback(
    (lang) => {
      if (isDisabledOptions) {
        return;
      }

      const isSpanish = lang === "sp";

      if (lang === i18n.language) {
        return;
      }

      updateLanguage(propertyId, apiKey, isSpanish);
      changeLanguage(lang);

      dispatch(setIsSpanishLanguage(isSpanish));
      dispatch(addNewMessage([{ message: `Language switched to ${isSpanish ? "Spanish" : "English"}`, speaker: "bot", showLangChangeAlert: true }]));

      formatMessageEdit();

      if (isSpanish) {
        createItemRecord(propertyId, SSID, "spanish conversions");
      }
    },
    [isSpanishCurrentLanguage, propertyId, apiKey, SSID, dispatch, isDisabledOptions]
  );

  useEffect(() => {
    const isSpanishSelected = i18n.language === "sp";

    dispatch(setIsSpanishLanguage(isSpanishSelected));
  }, [dispatch, i18n.language]);

  return (
    <div className="hayley-flex hayley-items-center hayley-gap-3">
      <div className="rounded-[1px] hayley-h-4 hayley-w-px hayley-bg-[#FFFFFF99]" />

      <div data-testid="language_switcher" className="hayley-flex hayley-rounded-[13px] hayley-bg-white hayley-p-[2px]">
        <CustomTooltip title="Switch to English">
          <Box
            component="p"
            data-testid="en_language_switcher"
            className="hayley-m-0 hayley-cursor-pointer hayley-rounded-xl hayley-px-2 hayley-py-[2px] hayley-text-xs hayley-leading-[18px]"
            onClick={() => handleLanguageChange("en")}
            sx={
              isEnglishCurrentLanguage
                ? {
                    backgroundColor,
                    fontWeight: 700,
                    color: "white",
                  }
                : { color: backgroundColor, "&:hover": { backgroundColor: `${backgroundColor}1A` } }
            }
          >
            EN
          </Box>
        </CustomTooltip>

        <CustomTooltip title="Switch to Spanish">
          <Box
            component="p"
            data-testid="es_language_switcher"
            className="hayley-m-0 hayley-cursor-pointer hayley-rounded-xl hayley-px-2 hayley-py-[2px] hayley-text-xs hayley-leading-[18px]"
            onClick={() => handleLanguageChange("sp")}
            sx={
              isSpanishCurrentLanguage
                ? {
                    backgroundColor,
                    fontWeight: 700,
                    color: "white",
                  }
                : { color: backgroundColor, "&:hover": { backgroundColor: `${backgroundColor}1A` } }
            }
          >
            ES
          </Box>
        </CustomTooltip>
      </div>

      <div className="rounded-[1px] hayley-h-4 hayley-w-px hayley-bg-[#FFFFFF99]" />
    </div>
  );
};
